import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { Chip } from '@column/column-ui-kit';

import { ROUTE } from '~/app/routes';
import { EmptyState, Loading, PageHeader } from '~/components';
import { RelativeTime } from '~/elements';
import { useNavigate } from '~/lib/navigation';
import { CardProgram, CardProgramRepository } from '~/repositories/CardProgramRepository';
import { Card, CardRepository } from '~/repositories/CardRepository';
import { EntityRepository, EntityWithDetails } from '~/repositories/EntityRepository';
import { useSessionStore } from '~/stores/Session';

const TableContainer = styled.div`
  padding: 0 var(--page-margin);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.secondary.blendToBackground(600)};
  border-bottom: 1px solid ${({ theme }) => theme.secondary.blendToBackground(100)};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Td = styled.td`
  padding: 16px;
  font-size: 14px;
  color: ${({ theme }) => theme.foreground};
  border-bottom: 1px solid ${({ theme }) => theme.secondary.blendToBackground(100)};
`;

const Tr = styled.tr`
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.secondary.blendToBackground(50)};
  }
`;

const StatusChip = styled(Chip)<{ $status: string }>`
  text-transform: capitalize;
`;

const TypeChip = styled(Chip)`
  text-transform: capitalize;
`;

const formatStatus = (status: string): string => {
  if (!status) return '-';
  return status.toLowerCase().replace(/_/g, ' ');
};

const formatType = (type: string): string => {
  if (!type) return '-';
  return type.toLowerCase().replace(/_/g, ' ');
};

// expiresAt is already in MM/YY format from the API, render directly
const formatExpiresAt = (expiresAt: string): string => {
  return expiresAt || '-';
};

export const PageCardsOverview: React.FC = () => {
  const navigate = useNavigate();
  const { currentPlatform, isSandbox } = useSessionStore();
  const [cards, setCards] = useState<Card[]>([]);
  const [cardProgramsMap, setCardProgramsMap] = useState<Map<string, CardProgram>>(new Map());
  const [entitiesMap, setEntitiesMap] = useState<Map<string, EntityWithDetails>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const [cardsResponse, programsResponse] = await Promise.all([
          CardRepository.listCards(),
          CardProgramRepository.listCardPrograms(),
        ]);

        setCards(cardsResponse.cards || []);

        const programsMap = new Map<string, CardProgram>();
        (programsResponse.cardPrograms || []).forEach((program) => {
          programsMap.set(program.id, program);
        });
        setCardProgramsMap(programsMap);

        // Fetch entity information for each unique authorized_user_entity_id
        const uniqueEntityIds = new Set<string>();
        (cardsResponse.cards || []).forEach((card) => {
          if (card.authorizedUserEntityId) {
            uniqueEntityIds.add(card.authorizedUserEntityId);
          }
        });

        const entityPromises = Array.from(uniqueEntityIds).map(async (entityId) => {
          try {
            const entity = await EntityRepository.get({ id: entityId });
            return { entityId, entity };
          } catch (err) {
            console.error(`Error fetching entity ${entityId}:`, err);
            return null;
          }
        });

        const entityResults = await Promise.all(entityPromises);
        const fetchedEntitiesMap = new Map<string, EntityWithDetails>();
        entityResults.forEach((result) => {
          if (result) {
            fetchedEntitiesMap.set(result.entityId, result.entity);
          }
        });
        setEntitiesMap(fetchedEntitiesMap);
      } catch (err) {
        console.error('Error fetching cards:', err);
        setError('Failed to load cards');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [currentPlatform?.id, isSandbox]);

  const getCardProgramName = (cardProgramId: string): string => {
    const program = cardProgramsMap.get(cardProgramId);
    return program?.description || cardProgramId || '-';
  };

  const getAuthorizedUserName = (entityId: string): string => {
    const entity = entitiesMap.get(entityId);
    if (!entity?.personDetails) {
      return '-';
    }
    const firstName = entity.personDetails.firstName || '';
    const lastName = entity.personDetails.lastName || '';
    return `${firstName} ${lastName}`.trim() || '-';
  };

  const renderTable = () => {
    if (cards.length === 0) {
      return <EmptyState headline="No cards" text="No cards found." />;
    }

    return (
      <Table>
        <thead>
          <tr>
            <Th>Authorized User</Th>
            <Th>Card Program</Th>
            <Th>BIN</Th>
            <Th>Last 4</Th>
            <Th>Expires</Th>
            <Th>Status</Th>
            <Th>Type</Th>
            <Th>Created</Th>
          </tr>
        </thead>
        <tbody>
          {cards.map((card) => (
            <Tr key={card.id} onClick={() => navigate(`${ROUTE.CARDS}/edit/${card.id}`)}>
              <Td>{getAuthorizedUserName(card.authorizedUserEntityId)}</Td>
              <Td>{getCardProgramName(card.cardProgramId)}</Td>
              <Td>{card.bin || '-'}</Td>
              <Td>{card.lastFourDigits || '-'}</Td>
              <Td>{formatExpiresAt(card.expiresAt)}</Td>
              <Td>
                <StatusChip $status={card.status}>{formatStatus(card.status)}</StatusChip>
              </Td>
              <Td>
                <TypeChip>{formatType(card.type)}</TypeChip>
              </Td>
              <Td>{card.createdAt ? <RelativeTime timestamp={card.createdAt} /> : '-'}</Td>
            </Tr>
          ))}
        </tbody>
      </Table>
    );
  };

  return (
    <>
      <PageHeader text="Cards" border={false} />
      <Loading isLoading={isLoading}>
        {error ? <EmptyState headline="Error" text={error} /> : <TableContainer>{renderTable()}</TableContainer>}
      </Loading>
    </>
  );
};
